import React, { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CountrySelect } from "@/components/CountrySelect";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const FIELDS = ["full_name", "current_title", "current_company", "headline", "city"];
const toForm = (c) => ({ ...Object.fromEntries(FIELDS.map((k) => [k, c[k] || ""])), country_code: c.country_code || "", years_experience: c.years_experience ?? "",
  skills: (c.skills || []).map((s) => s.name).join(", "), industries: (c.industries || []).join(", "), summary: c.summary || "" });
const list = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export function CandidateEditDialog({ id, onClose, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (id) { setF(null); api.get(`/candidates/${id}`).then((r) => setF(toForm(r.data))).catch((e) => toast.error(errorDetail(e))); } }, [id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/candidates/${id}`, { ...f, country_code: f.country_code || null, years_experience: f.years_experience === "" ? null : Number(f.years_experience),
        skills: list(f.skills), industries: list(f.industries), summary: f.summary || null });
      toast.success(t("Profile updated")); onSaved(); onClose();
    } catch (e) { toast.error(errorDetail(e)); } finally { setBusy(false); }
  };
  const L = (k, label, props = {}) => <label className="text-xs space-y-1 block"><span className="text-muted-foreground">{label}</span><Input value={f[k]} onChange={set(k)} className="h-9" data-testid={`edit-${k}`} {...props} /></label>;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="candidate-edit-dialog">
        <DialogHeader><DialogTitle>{t("Edit profile")}</DialogTitle></DialogHeader>
        {!f ? <div className="h-40 rounded-lg bg-muted shimmer" /> : (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">{L("full_name", t("Full name"))}{L("current_title", t("Current title"))}{L("current_company", t("Company"))}{L("city", t("City"))}
              <label className="text-xs space-y-1 block"><span className="text-muted-foreground">{t("Country")}</span><CountrySelect value={f.country_code} onChange={(v) => setF({ ...f, country_code: v })} testid="edit-country" /></label>
              {L("years_experience", t("Years of experience"), { type: "number", min: 0, max: 60 })}</div>
            {L("headline", t("Headline"))}
            {L("skills", t("Skills (comma separated)"))}
            {L("industries", t("Industries (comma separated)"))}
            <label className="text-xs space-y-1 block"><span className="text-muted-foreground">{t("Summary")}</span><Textarea rows={4} value={f.summary} onChange={set("summary")} data-testid="edit-summary" /></label>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose} data-testid="edit-cancel">{t("Cancel")}</Button>
              <Button disabled={busy || !f.full_name.trim()} onClick={save} data-testid="edit-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{t("Save")}</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
